import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useMediaQuery} from '@github-ui/use-media-query'
import {CheckCircleFillIcon, CircleIcon, CopilotIcon, XIcon} from '@primer/octicons-react'
import {Banner, Button, IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useRef, useState} from 'react'

import personaOnboardingDotPattern from '../../assets/persona-onboarding-dot-pattern.png'
import {usePersonaOnboarding, useSavePersonaOnboarding} from '../../contexts/PersonaOnboardingContext'
import {type CopilotPersona, type PersonaOnboardingUpdate, personas} from '../../data/personas'
import {
  reportPersonaOnboardingCompleted,
  reportPersonaOnboardingDismissed,
} from '../../utils/persona-onboarding-telemetry'
import styles from './PersonaOnboardingModal.module.css'

const welcomeMessage = "Welcome to GitHub Copilot. Tell us how you work, and we'll suggest relevant tasks."

export function PersonaOnboardingModal({
  login,
  returnFocusRef,
}: {
  login: string
  returnFocusRef: RefObject<HTMLTextAreaElement | null>
}) {
  const onboarding = usePersonaOnboarding()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const [persona, setPersona] = useState<CopilotPersona>(onboarding?.persona ?? 'software_development_it')
  const selectedPersonaRef = useRef<HTMLInputElement>(null)
  const saveInFlightRef = useRef(false)
  const {mutate, isPending, error} = useSavePersonaOnboarding((saved, update) => {
    sendEvent('dotcom_chat.activate', {
      target: update.status === 'completed' ? 'PERSONA_ONBOARDING_COMPLETED' : 'PERSONA_ONBOARDING_DISMISSED',
      mode: 'immersive',
    })
    if (update.status === 'completed' && !saved.show && saved.persona === update.persona) {
      reportPersonaOnboardingCompleted(update.persona)
    }
  })

  if (!onboarding?.enabled || !onboarding.show) return null

  const save = (update: PersonaOnboardingUpdate) => {
    if (saveInFlightRef.current) return false
    saveInFlightRef.current = true
    mutate(update, {
      onError: saveError => {
        announce(saveError.message, {assertive: true, element: selectedPersonaRef.current ?? undefined})
      },
      onSettled: () => {
        saveInFlightRef.current = false
      },
    })
    return true
  }

  const dismiss = () => {
    if (save({status: 'dismissed'})) reportPersonaOnboardingDismissed()
  }

  const skipButton = (
    <Button
      key="skip"
      variant="invisible"
      onClick={dismiss}
      inactive={isPending}
      aria-disabled={isPending}
      className={styles.Skip}
    >
      Skip guided setup
    </Button>
  )
  const submitButton = (
    <Button
      key="submit"
      type="submit"
      variant="primary"
      loading={isPending}
      loadingAnnouncement="Saving guided setup"
      className={styles.Submit}
    >
      Suggest tasks
    </Button>
  )

  return (
    <Dialog
      title="Welcome to GitHub Copilot"
      subtitle={welcomeMessage}
      width="792px"
      position={{narrow: 'fullscreen', regular: 'center'}}
      className={styles.Dialog}
      returnFocusRef={returnFocusRef}
      initialFocusRef={selectedPersonaRef}
      onClose={dismiss}
      renderHeader={({dialogLabelId, dialogDescriptionId, subtitle}) => (
        <div className={styles.Header}>
          <span id={dialogLabelId} className={styles.ScreenReaderOnly}>
            Welcome to GitHub Copilot
          </span>
          <span id={dialogDescriptionId} className={styles.ScreenReaderOnly}>
            {subtitle}
          </span>
          <IconButton
            icon={XIcon}
            aria-label="Close guided setup"
            variant="invisible"
            onClick={dismiss}
            inactive={isPending}
            aria-disabled={isPending}
          />
        </div>
      )}
      renderBody={() => (
        <div className={styles.Body}>
          <div className={styles.Introduction}>
            <CopilotIcon size={32} />
            <p className={styles.Greeting}>
              Hey, {login}!<span className={styles.Welcome}>{welcomeMessage}</span>
            </p>
            <img
              className={styles.Decoration}
              src={personaOnboardingDotPattern}
              alt=""
              aria-hidden="true"
              draggable="false"
              width={608}
              height={280}
            />
          </div>
          <form
            className={styles.Form}
            onSubmit={event => {
              event.preventDefault()
              save({status: 'completed', persona})
            }}
          >
            <fieldset className={styles.Options}>
              <legend className={styles.Question}>What type of work do you usually focus on?</legend>
              {personas.map(option => (
                <label key={option.id} className={styles.Option} data-selected={persona === option.id}>
                  <input
                    className={styles.Radio}
                    ref={persona === option.id ? selectedPersonaRef : undefined}
                    type="radio"
                    name="copilot-persona"
                    value={option.id}
                    checked={persona === option.id}
                    aria-disabled={isPending}
                    onClick={event => {
                      if (saveInFlightRef.current) event.preventDefault()
                    }}
                    onChange={() => {
                      if (!saveInFlightRef.current) setPersona(option.id)
                    }}
                  />
                  <span className={styles.OptionIcon} aria-hidden="true">
                    {persona === option.id ? <CheckCircleFillIcon /> : <CircleIcon />}
                  </span>
                  {option.label}
                </label>
              ))}
            </fieldset>
            {error && <Banner variant="critical" title={error.message} />}
            <div className={styles.Actions}>
              {isMobile ? submitButton : skipButton}
              {isMobile ? skipButton : submitButton}
            </div>
          </form>
        </div>
      )}
    />
  )
}
